// The dashboard shell: a venue switcher in the top bar (every venue of an owner, only the assigned venues of a
// manager), the venue's facts, its pickup points with their QR link, staff screen, the TV board's signed link with
// copy and revoke (issue #16), the board's optional second voice language (issue #19) and the A4 / A5 poster
// downloads (issue #13, `Poster.tsx`), the staff PIN with the signed-in devices (issue #12), and the account row with
// the backup e-mail for the sign-in code (issue #23, `BackupEmail.tsx`). The settings page (issue #11,
// `#/dashboard/:venue/settings`) renders inside the same shell; the metrics (issue #40) mount here too.
import { useEffect, useState, type FormEvent } from 'react';
import {
  LANG_DIR,
  LANG_LABELS,
  STAFF_DEVICE,
  SUPPORTED_LANGS,
  UI,
  WEEK_ORDER,
  can,
  deviceStatusCopy,
  fill,
  googleReviewUrl,
  lastSeenCopy,
  regeneratePinWarning,
  staffPinStatusCopy,
  type HoursRange,
  type Lang,
  type PosterSize,
} from '@ringring/core';
import { BackupEmail } from './BackupEmail';
import { SettingsForm } from './Settings';
import { PosterDownloads } from './Poster';
import { api, downloadPoster, errorText, type ApiError, type Me, type NewStaffPin, type PickupPoint, type StaffDevice, type Venue, type VenueSettingsBody } from './api';
import { dayLabel, formatRanges } from './hours';
import { LangPicker, Logo } from './lang';
import { Sidebar } from './Sidebar';
import { Icon } from './Icon';
import { Feedback } from './Feedback';

/** What the shell shows under the top bar: the venue's facts and pickup points, or its settings form. */
export type DashboardPage = 'home' | 'settings' | 'feedback';

const VENUE_KEY = 'rr_venue';

const LOCALES: Record<Lang, string> = { fr: 'fr-MA', ar: 'ar-MA', en: 'en-GB' };

/** A date in the venue's time zone, in the page language; digits are shown as an LTR run by the caller. */
export function formatDate(lang: Lang, ts: number): string {
  return new Intl.DateTimeFormat(LOCALES[lang], { dateStyle: 'medium', timeZone: 'Africa/Casablanca' }).format(ts);
}

function rememberVenue(id: string): void {
  try {
    localStorage.setItem(VENUE_KEY, id);
  } catch {
    // storage unavailable
  }
}

function rememberedVenue(): string | null {
  try {
    return localStorage.getItem(VENUE_KEY);
  } catch {
    return null;
  }
}

export interface DashboardViewProps {
  lang: Lang;
  setLang: (l: Lang) => void;
  me: Me;
  venueId: string;
  venue: Venue | null;
  /** The API code of the last refused call, worded at render time in the screen language (`errorText`). */
  error: string | null;
  copiedId: string | null;
  /** Defaults to the venue's facts and pickup points. */
  page?: DashboardPage;
  settingsBusy?: boolean;
  settingsSaved?: boolean;
  onSwitch: (id: string) => void;
  onAddPickupPoint: (name: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onArchive: (id: string, archived: boolean) => Promise<void>;
  /** The pickup point whose voice language was just saved; its row shows "✓ Enregistré" until the next change. */
  savedVoicePp: string | null;
  onVoiceLang: (id: string, lang: Lang | null) => Promise<void>;
  /** Copy a pickup point's customer link (the default) or its TV board link; `copiedId` is then its id, or `board:<id>`. */
  onCopy: (p: PickupPoint, what?: 'customer' | 'board') => void;
  /** Revoke the TV board link of a pickup point (issue #16); the refreshed venue carries the new link. */
  onRevokeBoardLink?: (id: string) => Promise<void>;
  /** The pickup point whose board link was just renewed; its block says so until the next action. */
  renewedBoardPp?: string | null;
  onSignOut: (everywhere: boolean) => void;
  /** The backup e-mail for the sign-in code was confirmed (the address) or removed (null). */
  onEmailChange: (email: string | null) => void;
  /** The venue's staff devices (issue #12) and the PIN just generated, shown once until dismissed. */
  devices: StaffDevice[];
  newPin: NewStaffPin | null;
  now: number;
  onGeneratePin: () => Promise<void>;
  onDismissPin: () => void;
  onRevokeDevice: (id: string) => Promise<void>;
  onSaveSettings?: (body: VenueSettingsBody) => Promise<void>;
  /** The venue logo on the settings page (issue #20): upload a file at once, or remove it. */
  onUploadLogo?: (file: File) => Promise<void>;
  onRemoveLogo?: () => Promise<void>;
  /** Fetches and saves the A4 or A5 poster of a pickup point (issue #13); throws the ApiError of a refusal. */
  onDownloadPoster?: (pickupPointId: string, size: PosterSize, fileName: string) => Promise<void>;
}

const asyncNoop = async () => undefined;

/** The shell without any fetching, so it can be rendered with data in tests. */
export function DashboardView({
  lang,
  setLang,
  me,
  venueId,
  venue,
  error,
  copiedId,
  page = 'home',
  settingsBusy = false,
  settingsSaved = false,
  onSwitch,
  onAddPickupPoint,
  onRename,
  onArchive,
  savedVoicePp,
  onVoiceLang,
  onCopy,
  onRevokeBoardLink = asyncNoop,
  renewedBoardPp = null,
  onSignOut,
  onEmailChange,
  onSaveSettings = asyncNoop,
  onUploadLogo = asyncNoop,
  onRemoveLogo = asyncNoop,
  onDownloadPoster = asyncNoop,
  devices,
  newPin,
  now,
  onGeneratePin,
  onDismissPin,
  onRevokeDevice,
}: DashboardViewProps) {
  const t = UI[lang];
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const isOwner = venue ? me.owner_of_orgs.includes(venue.org_id) : false;
  const active = venue?.pickup_points.filter((p) => p.archived_at === null) ?? [];
  const archived = venue?.pickup_points.filter((p) => p.archived_at !== null) ?? [];

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } finally {
      setBusy(false);
    }
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    void run(async () => {
      await onAddPickupPoint(newName);
      setNewName('');
    });
  };

  const saveName = (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    void run(async () => {
      await onRename(editing.id, editing.name);
      setEditing(null);
    });
  };

  const point = (p: PickupPoint) => (
    <div className="card pp" key={p.id}>
      <div className="counter-heading">
        <span className="counter-icon">
          <Icon name="location" />
        </span>
        <div>
          <h3>{p.name}</h3>
          <p className="muted">{p.archived_at === null ? t.design_counter_intro : t.archived}</p>
        </div>
        <span className="counter-index" aria-hidden="true">
          <Icon name="orders" />
        </span>
      </div>
      <div className="counter-actions">
        <a className="btn" href={`#/staff/${encodeURIComponent(p.id)}`}>
          <Icon name="orders" />
          {t.staff_screen}
          <Icon name="arrow" className="directional" />
        </a>
        <a className="btn secondary" href={p.board_url} target="_blank" rel="noreferrer">
          <Icon name="screen" />
          {t.tv_board}
          <Icon name="external" />
        </a>
        <a className="btn secondary" href={p.customer_url} target="_blank" rel="noreferrer">
          <Icon name="phone" />
          {t.customer_url}
          <Icon name="external" />
        </a>
      </div>
      <details className="counter-details">
        <summary>
          <Icon name="settings" />
          {t.design_counter_tools}
          <Icon name="plus" />
        </summary>
        <div className="details-content">
          <div className="row">
            <strong>{p.name}</strong>
            {p.archived_at !== null ? (
              <span className="tag">
                <span aria-hidden="true">⊘</span> {t.archived}
              </span>
            ) : null}
            <span className="spacer" />
            {p.archived_at === null ? (
              <button className="btn secondary" type="button" onClick={() => setEditing({ id: p.id, name: p.name })} disabled={busy}>
                {t.rename}
              </button>
            ) : null}
            <button className="btn secondary" type="button" onClick={() => void run(() => onArchive(p.id, p.archived_at === null))} disabled={busy}>
              {p.archived_at === null ? t.archive : t.unarchive}
            </button>
          </div>
          {editing?.id === p.id ? (
            <form className="row" onSubmit={saveName}>
              <label className="sr-only" htmlFor={`rename-${p.id}`}>
                {t.pickup_point_name}
              </label>
              <input
                id={`rename-${p.id}`}
                value={editing.name}
                onChange={(e) => setEditing({ id: p.id, name: e.target.value })}
                maxLength={80}
                autoFocus
                style={{ maxWidth: 320 }}
              />
              <button className="btn" type="submit" disabled={busy}>
                {t.save}
              </button>
              <button className="btn secondary" type="button" onClick={() => setEditing(null)}>
                {t.cancel}
              </button>
            </form>
          ) : null}
          <div className="row link-row">
            <span className="muted">{t.customer_url}</span>
            <code>
              <bdi dir="ltr">{p.customer_url}</bdi>
            </code>
          </div>
          <div className="row">
            <button className="btn secondary" type="button" onClick={() => onCopy(p)}>
              {copiedId === p.id ? `✓ ${t.copied}` : t.copy_link}
            </button>
            <a className="btn secondary" href={p.customer_url} target="_blank" rel="noreferrer">
              {t.open_link}
            </a>
            <a className="btn secondary" href={`#/staff/${encodeURIComponent(p.id)}`}>
              {t.staff_screen}
            </a>
          </div>
          <BoardLinkSetting
            lang={lang}
            pp={p}
            canRevoke={venue !== null && can(venue.role, 'edit_settings')}
            copied={copiedId === `board:${p.id}`}
            renewed={renewedBoardPp === p.id}
            busy={busy}
            onCopy={() => onCopy(p, 'board')}
            onRevoke={() => void run(() => onRevokeBoardLink(p.id))}
          />
          {venue && p.archived_at === null && can(venue.role, 'edit_settings') ? (
            <>
              <VoiceLangSetting lang={lang} venueDefault={venue.default_lang} pp={p} saved={savedVoicePp === p.id} onChange={(l) => void run(() => onVoiceLang(p.id, l))} />
              <PosterDownloads lang={lang} venueName={venue.name} pp={p} onDownload={(size, fileName) => onDownloadPoster(p.id, size, fileName)} />
            </>
          ) : null}
        </div>
      </details>
    </div>
  );

  return (
    <div className="app workspace dashboard" lang={lang} dir={LANG_DIR[lang]}>
      <Sidebar lang={lang} venueId={venueId} venueName={venue?.name} page={page} />
      <div className="workspace-body">
        <header className="topbar workspace-topbar">
          <span className="eyebrow workspace-breadcrumb">{t.design_restaurant_space}</span>
          <label className="switcher">
            <span className="sr-only">{t.venue_switcher}</span>
            <select value={venueId} onChange={(e) => onSwitch(e.target.value)} aria-label={t.venue_switcher}>
              {me.venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                  {v.city ? ` · ${v.city}` : ''}
                </option>
              ))}
            </select>
          </label>
          <LangPicker lang={lang} setLang={setLang} />
        </header>
        <main className="workspace-content">
          {error ? (
            <div className="banner" role="alert">
              {errorText(lang, error)}
            </div>
          ) : null}
          {venue && page === 'feedback' ? <Feedback key={venue.id} lang={lang} venueId={venue.id} reviewUrl={venue.google_link_enabled && venue.google_place_id ? googleReviewUrl(venue.google_place_id) : null} /> : null}
          {venue && page === 'settings' ? (
            <>
              <div className="row">
                <h1 className="display">{t.settings_title}</h1>
                <span className="tag">{venue.role === 'owner' ? t.role_owner : t.role_manager}</span>
              </div>
              <p className="muted">{t.settings_intro}</p>
              <div className="row">
                <a className="btn secondary" href={`#/dashboard/${encodeURIComponent(venue.id)}`}>
                  {t.back_to_dashboard}
                </a>
              </div>
              <SettingsForm
                key={venue.id}
                lang={lang}
                venue={venue}
                busy={settingsBusy}
                saved={settingsSaved}
                onSave={onSaveSettings}
                onUploadLogo={onUploadLogo}
                onRemoveLogo={onRemoveLogo}
                onDownloadPoster={onDownloadPoster}
              />
            </>
          ) : null}
          {venue && page === 'home' ? (
            <>
              <div className="page-heading">
                <div>
                  <p className="eyebrow">{t.dashboard}</p>
                  <h1 className="display">{t.design_service_heading}</h1>
                  <p className="muted">{t.design_service_intro}</p>
                </div>
                <span className="tag role-tag">
                  <Icon name="user" />
                  {venue.role === 'owner' ? t.role_owner : t.role_manager}
                </span>
              </div>
              <section className="venue-overview" aria-label={t.venue_details}>
                <div className="venue-overview-title">
                  <span className="eyebrow">{t.venue_details}</span>
                  <h2>{venue.name}</h2>
                  <span>
                    <Icon name="location" />
                    {venue.city || '—'}
                  </span>
                </div>
                <div className="overview-stat">
                  <Icon name="location" />
                  <strong>
                    <bdi dir="ltr">{active.length}</bdi>
                  </strong>
                  <span>{t.design_active_counters}</span>
                </div>
                <div className="overview-stat">
                  <Icon name="screen" />
                  <strong>
                    <bdi dir="ltr">{devices.filter((d) => d.status === 'active').length}</bdi>
                  </strong>
                  <span>{t.design_team_devices}</span>
                </div>
                <div className="overview-stat">
                  <Icon name="globe" />
                  <strong>
                    <bdi dir="ltr">{venue.languages.length}</bdi>
                  </strong>
                  <span>{t.design_guest_languages}</span>
                </div>
              </section>
              <details className="venue-details disclosure-panel">
                <summary>
                  <Icon name="settings" />
                  {t.venue_details}
                  <Icon name="plus" />
                </summary>
                <section className="card" aria-labelledby="venue-facts">
                  <div className="row">
                    <h2 id="venue-facts">{t.venue_details}</h2>
                    <span className="spacer" />
                    <a className="btn secondary" href={`#/dashboard/${encodeURIComponent(venue.id)}/settings`}>
                      {t.settings}
                    </a>
                  </div>
                  <dl className="facts">
                    <dt>{t.city}</dt>
                    <dd>{venue.city ?? '—'}</dd>
                    <dt>{t.mode_label}</dt>
                    <dd>{venue.number_mode === 'scan' ? t.mode_scan : t.mode_ticket}</dd>
                    <dt>{t.languages_label}</dt>
                    <dd>{venue.languages.map((l) => LANG_LABELS[l]).join(' · ')}</dd>
                    <dt>{t.default_lang_label}</dt>
                    <dd lang={venue.default_lang}>{LANG_LABELS[venue.default_lang]}</dd>
                    <dt>{t.registrations_label}</dt>
                    <dd>
                      <span className={`tag ${venue.registrations_paused ? 'call' : 'ready'}`}>
                        <span aria-hidden="true">{venue.registrations_paused ? '⏸' : '✓'}</span> {venue.registrations_paused ? t.paused_tag : t.active_tag}
                      </span>
                    </dd>
                    <dt>{t.hours_label}</dt>
                    <dd>
                      <div className="hours-list">
                        {WEEK_ORDER.map((d) => (
                          <span key={d} className="hours-day">
                            <span>{dayLabel(lang, d)}</span>
                            <HoursOf lang={lang} ranges={venue.opening_hours ? venue.opening_hours[d] : []} />
                          </span>
                        ))}
                      </div>
                    </dd>
                  </dl>
                </section>
              </details>
              <section className="pickup-section" aria-labelledby="pickup-points">
                <h2 id="pickup-points">{t.pickup_points}</h2>
                <p className="muted">{t.pickup_points_intro}</p>
                <div className="counter-grid">{active.map(point)}</div>
                <details className="add-counter disclosure-panel">
                  <summary>
                    <Icon name="plus" />
                    {t.add_pickup_point}
                  </summary>
                  <form className="card" onSubmit={add}>
                    <label htmlFor="new-pp">{t.pickup_point_name}</label>
                    <div className="row">
                      <input
                        id="new-pp"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        maxLength={80}
                        placeholder={t.pickup_point_default}
                        style={{ maxWidth: 320 }}
                      />
                      <button className="btn" type="submit" disabled={busy || !newName.trim()}>
                        {t.add_pickup_point}
                      </button>
                    </div>
                  </form>
                </details>
                {archived.length > 0 ? (
                  <>
                    <h3>{t.archived_points}</h3>
                    {archived.map(point)}
                  </>
                ) : null}
              </section>
              <StaffAccess
                lang={lang}
                venue={venue}
                devices={devices}
                newPin={newPin}
                now={now}
                busy={busy}
                onGenerate={() => run(onGeneratePin)}
                onDismiss={onDismissPin}
                onRevoke={(id) => run(() => onRevokeDevice(id))}
              />
              {isOwner ? (
                <div className="row">
                  <a className="btn secondary" href={`#/orgs/${encodeURIComponent(venue.org_id)}/venues/new`}>
                    {t.add_venue}
                  </a>
                </div>
              ) : null}
            </>
          ) : null}
          <section className="card" aria-labelledby="account">
            <h2 id="account">{t.account}</h2>
            <p className="muted">
              {t.signed_in_as} <bdi dir="ltr">{me.user.phone_display}</bdi>
              {me.user.name ? ` · ${me.user.name}` : ''}
            </p>
            <div className="row">
              <button className="btn secondary" type="button" onClick={() => onSignOut(false)}>
                {t.sign_out}
              </button>
              <button className="btn secondary" type="button" onClick={() => onSignOut(true)}>
                {t.sign_out_all}
              </button>
            </div>
          </section>
          <BackupEmail t={t} lang={lang} email={me.user.email} onChange={onEmailChange} />
        </main>
      </div>
    </div>
  );
}

/**
 * The TV board's signed link of one pickup point (issue #16, US-43): the link as an LTR run, a 48 px copy button, the
 * link itself to open, and for managers and owners "Révoquer ce lien", which asks first (the screen on the old link
 * stops within a minute) and then shows the new link with a confirmation. Archived pickup points keep theirs: their
 * board still runs. Exported for the render test.
 */
export function BoardLinkSetting({
  lang,
  pp,
  canRevoke,
  copied,
  renewed,
  busy,
  onCopy,
  onRevoke,
  startConfirming = false,
}: {
  lang: Lang;
  pp: Pick<PickupPoint, 'id' | 'board_url'>;
  canRevoke: boolean;
  copied: boolean;
  renewed: boolean;
  busy: boolean;
  onCopy: () => void;
  onRevoke: () => void;
  /** Open on the confirmation line (render tests). */
  startConfirming?: boolean;
}) {
  const t = UI[lang];
  const [confirming, setConfirming] = useState(startConfirming);
  const revoke = () => {
    setConfirming(false);
    onRevoke();
  };
  return (
    <div className="board-link">
      <div className="row link-row">
        <span className="muted">{t.board_link}</span>
        <code>
          <bdi dir="ltr">{pp.board_url}</bdi>
        </code>
      </div>
      <p className="muted">{t.board_link_intro}</p>
      {renewed ? (
        <p className="tag ready" role="status">
          <span aria-hidden="true">✓</span> {t.board_link_renewed}
        </p>
      ) : null}
      <div className="row">
        <button className="btn secondary" type="button" onClick={onCopy}>
          {copied ? `✓ ${t.copied}` : t.copy_board_link}
        </button>
        <a className="btn secondary" href={pp.board_url} target="_blank" rel="noreferrer">
          {t.tv_board}
        </a>
        {canRevoke && !confirming ? (
          <button className="btn secondary" type="button" onClick={() => setConfirming(true)} disabled={busy}>
            {t.revoke_board_link}
          </button>
        ) : null}
      </div>
      {canRevoke && confirming ? (
        <div className="row confirm-line" role="group" aria-label={t.revoke_board_link_warning}>
          <span>
            <span aria-hidden="true">⚠</span> {t.revoke_board_link_warning}
          </span>
          <button className="btn" type="button" onClick={revoke} disabled={busy}>
            {t.confirm_revoke_board_link}
          </button>
          <button className="btn secondary" type="button" onClick={() => setConfirming(false)} autoFocus>
            {t.cancel}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The staff PIN and the devices that entered it (issue #12, US-9 and US-39). The PIN is shown once, right after
 * generation, as a large LTR run; regenerating asks first because it signs every device out. Each device row
 * carries its label, a status tag with a symbol, when it was last seen, and a 48 px Révoquer on signed-in ones.
 * Exported for the render test.
 */
export function StaffAccess({
  lang,
  venue,
  devices,
  newPin,
  now,
  busy,
  onGenerate,
  onDismiss,
  onRevoke,
}: {
  lang: Lang;
  venue: Venue;
  devices: StaffDevice[];
  newPin: NewStaffPin | null;
  now: number;
  busy: boolean;
  onGenerate: () => void;
  onDismiss: () => void;
  onRevoke: (id: string) => void;
}) {
  const t = UI[lang];
  const [confirming, setConfirming] = useState(false);
  const active = devices.filter((d) => d.status === 'active');
  const former = devices.filter((d) => d.status !== 'active');
  const generate = () => {
    setConfirming(false);
    onGenerate();
  };
  const warning = regeneratePinWarning(lang, active.length);
  return (
    <section className="card" aria-labelledby="staff-pin">
      <h2 id="staff-pin">{t.staff_pin_section}</h2>
      <p className="muted">{t.staff_pin_intro}</p>
      {newPin ? (
        <div className="pin-reveal" role="status" aria-live="polite">
          <strong>{t.new_pin_title}</strong>
          <div className="pin-value">
            <bdi dir="ltr">{newPin.pin}</bdi>
          </div>
          <p>{t.new_pin_note}</p>
          <button className="btn" type="button" onClick={onDismiss}>
            {t.pin_noted}
          </button>
        </div>
      ) : (
        <>
          <p>{staffPinStatusCopy(lang, venue.has_staff_pin, venue.staff_pin_set_at, (ts) => formatDate(lang, ts))}</p>
          {confirming ? (
            <div className="row confirm-line" role="group" aria-label={warning}>
              <span>
                <span aria-hidden="true">⚠</span> {warning}
              </span>
              <button className="btn" type="button" onClick={generate} disabled={busy}>
                {t.confirm_regenerate}
              </button>
              <button className="btn secondary" type="button" onClick={() => setConfirming(false)} autoFocus>
                {t.cancel}
              </button>
            </div>
          ) : (
            <div className="row">
              <button className="btn" type="button" onClick={venue.has_staff_pin ? () => setConfirming(true) : generate} disabled={busy}>
                {venue.has_staff_pin ? t.regenerate_pin : t.generate_pin}
              </button>
            </div>
          )}
        </>
      )}
      <h3>{t.devices}</h3>
      <p className="muted">{t.devices_intro}</p>
      {active.length === 0 ? <p className="muted">{t.no_devices}</p> : active.map((d) => <Device key={d.id} lang={lang} d={d} now={now} busy={busy} onRevoke={onRevoke} />)}
      {former.length > 0 ? (
        <>
          <h3>{t.former_devices}</h3>
          {former.map((d) => (
            <Device key={d.id} lang={lang} d={d} now={now} busy={busy} onRevoke={onRevoke} />
          ))}
        </>
      ) : null}
    </section>
  );
}

/** One device: label, status tag (symbol and words), last activity, counter, first sign-in; Révoquer only while signed in. */
function Device({ lang, d, now, busy, onRevoke }: { lang: Lang; d: StaffDevice; now: number; busy: boolean; onRevoke: (id: string) => void }) {
  const t = UI[lang];
  const status = deviceStatusCopy(lang, d.status, d.revoked_reason);
  return (
    <div className="row device">
      <strong>{d.label ?? t.unknown_device}</strong>
      <span className={`tag ${d.status === 'active' ? 'ready' : ''}`}>
        <span aria-hidden="true">{status.symbol}</span> {status.label}
      </span>
      <span className="muted">
        {t.last_seen} · {lastSeenCopy(lang, d.last_seen_at, now)}
      </span>
      {d.pickup_point?.name ? <span className="muted">{fill(t.signed_in_from, { pickup_point: d.pickup_point.name })}</span> : null}
      <span className="muted">
        {t.first_seen} · <bdi dir="ltr">{formatDate(lang, d.first_seen_at)}</bdi>
      </span>
      <span className="spacer" />
      {d.status === 'active' ? (
        <button className="btn secondary" type="button" onClick={() => onRevoke(d.id)} disabled={busy}>
          {t.revoke}
        </button>
      ) : null}
    </div>
  );
}

/** One day's hours: the ranges as an LTR run (digits keep their order in Arabic), or the closed label in the screen language. */
function HoursOf({ lang, ranges }: { lang: Lang; ranges: readonly HoursRange[] }) {
  return ranges.length === 0 ? <span>{formatRanges(lang, ranges)}</span> : <bdi dir="ltr">{formatRanges(lang, ranges)}</bdi>;
}

/** Loads the account and the venue named in the URL (or the remembered one, or the first), then renders the shell. */
export function Dashboard({ lang, setLang, venueId, page = 'home' }: { lang: Lang; setLang: (l: Lang) => void; venueId: string | null; page?: DashboardPage }) {
  const [me, setMe] = useState<Me | null>(null);
  const [venue, setVenue] = useState<Venue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [savedVoicePp, setSavedVoicePp] = useState<string | null>(null);
  const [renewedBoardPp, setRenewedBoardPp] = useState<string | null>(null);
  const [devices, setDevices] = useState<StaffDevice[]>([]);
  const [newPin, setNewPin] = useState<NewStaffPin | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const fail = (e: ApiError) => {
    if (e.status === 401) window.location.hash = '#/signin';
    else setError(e.code ?? 'internal');
  };

  useEffect(() => {
    api<Me>('/auth/me')
      .then((m) => {
        if (m.venues.length === 0) {
          window.location.hash = '#/setup';
          return;
        }
        setMe(m);
      })
      .catch(fail);
  }, []);

  // The venue shown: the one in the URL when the user can open it, else the remembered one, else the first.
  const selected = me ? (me.venues.find((v) => v.id === venueId) ?? me.venues.find((v) => v.id === rememberedVenue()) ?? me.venues[0]).id : null;

  useEffect(() => {
    if (!selected) return;
    if (selected !== venueId) {
      window.location.hash = `#/dashboard/${encodeURIComponent(selected)}`;
      return;
    }
    rememberVenue(selected);
    setVenue(null);
    setRenewedBoardPp(null);
    setDevices([]);
    setNewPin(null);
    setSettingsSaved(false);
    api<Venue>(`/account/venues/${encodeURIComponent(selected)}`)
      .then(setVenue)
      .catch(fail);
    void loadDevices(selected);
  }, [selected, venueId]);

  const loadDevices = async (id: string) => {
    try {
      setDevices((await api<{ devices: StaffDevice[] }>(`/account/venues/${encodeURIComponent(id)}/staff-devices`)).devices);
      setNow(Date.now());
    } catch (e) {
      fail(e as ApiError);
    }
  };

  // The device list follows the counters: a phone that just entered the PIN shows up, "last seen" moves, a revoked one changes status.
  useEffect(() => {
    if (!selected) return;
    const id = window.setInterval(() => void loadDevices(selected), STAFF_DEVICE.checkEveryMs);
    return () => window.clearInterval(id);
  }, [selected]);

  /**
   * One PATCH with the whole form (issue #11). The refreshed venue comes back in the response; the switcher is
   * reloaded too so a renamed venue or a mode change shows there. A refusal is thrown to the form, which shows its
   * copy; a lost session goes to the sign-in page.
   */
  const saveSettings = async (body: VenueSettingsBody) => {
    if (!selected) return;
    setSettingsBusy(true);
    setSettingsSaved(false);
    try {
      const v = await api<Venue>(`/account/venues/${encodeURIComponent(selected)}`, { method: 'PATCH', body: JSON.stringify(body) });
      setVenue(v);
      setMe(await api<Me>('/auth/me'));
      setSettingsSaved(true);
      window.setTimeout(() => setSettingsSaved(false), 4000);
    } catch (e) {
      if ((e as ApiError).status === 401) {
        window.location.hash = '#/signin';
        return;
      }
      throw e;
    } finally {
      setSettingsBusy(false);
    }
  };

  /**
   * The logo (issue #20) goes up on its own, the file itself as the body; the refreshed venue comes back with the new
   * `logo_url`. A refusal is thrown to the logo card, which shows its copy; a lost session goes to the sign-in page.
   */
  const logoRequest = async (init: RequestInit) => {
    if (!selected) return;
    try {
      setVenue(await api<Venue>(`/account/venues/${encodeURIComponent(selected)}/logo`, init));
    } catch (e) {
      if ((e as ApiError).status === 401) {
        window.location.hash = '#/signin';
        return;
      }
      throw e;
    }
  };
  const uploadLogo = (file: File) => logoRequest({ method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
  const removeLogo = () => logoRequest({ method: 'DELETE' });

  const reload = async () => {
    if (!selected) return;
    setVenue(await api<Venue>(`/account/venues/${encodeURIComponent(selected)}`));
    setMe(await api<Me>('/auth/me'));
    await loadDevices(selected);
  };

  const guarded = async (work: () => Promise<void>) => {
    setError(null);
    try {
      await work();
    } catch (e) {
      fail(e as ApiError);
    }
  };

  if (!me || !selected) {
    return (
      <div className="app" lang={lang} dir={LANG_DIR[lang]}>
        <div className="topbar">
          <Logo />
          <LangPicker lang={lang} setLang={setLang} />
        </div>
        {error ? (
          <div className="banner" role="alert">
            {errorText(lang, error)}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <DashboardView
      lang={lang}
      setLang={setLang}
      me={me}
      venueId={selected}
      venue={venue}
      error={error}
      copiedId={copiedId}
      page={page}
      settingsBusy={settingsBusy}
      settingsSaved={settingsSaved}
      onSaveSettings={saveSettings}
      onUploadLogo={uploadLogo}
      onRemoveLogo={removeLogo}
      onSwitch={(id) => {
        window.location.hash = `#/dashboard/${encodeURIComponent(id)}${page === 'home' ? '' : `/${page}`}`;
      }}
      onAddPickupPoint={(name) =>
        guarded(async () => {
          await api(`/account/venues/${encodeURIComponent(selected)}/pickup-points`, { method: 'POST', body: JSON.stringify({ name }) });
          await reload();
        })
      }
      onRename={(id, name) =>
        guarded(async () => {
          await api(`/account/venues/${encodeURIComponent(selected)}/pickup-points/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ name }) });
          await reload();
        })
      }
      onArchive={(id, archived) =>
        guarded(async () => {
          await api(`/account/venues/${encodeURIComponent(selected)}/pickup-points/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ archived }) });
          await reload();
        })
      }
      savedVoicePp={savedVoicePp}
      onVoiceLang={(id, voiceLang) =>
        guarded(async () => {
          setSavedVoicePp(null);
          await api(`/account/venues/${encodeURIComponent(selected)}/pickup-points/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            body: JSON.stringify({ voice_lang_2: voiceLang }),
          });
          await reload();
          setSavedVoicePp(id);
        })
      }
      onCopy={(p, what = 'customer') => {
        const id = what === 'board' ? `board:${p.id}` : p.id;
        void navigator.clipboard?.writeText(what === 'board' ? p.board_url : p.customer_url).then(() => {
          setCopiedId(id);
          window.setTimeout(() => setCopiedId(null), 2000);
        });
      }}
      renewedBoardPp={renewedBoardPp}
      onRevokeBoardLink={(id) =>
        guarded(async () => {
          setRenewedBoardPp(null);
          await api(`/account/venues/${encodeURIComponent(selected)}/pickup-points/${encodeURIComponent(id)}/board-link/revoke`, { method: 'POST' });
          await reload();
          setRenewedBoardPp(id);
        })
      }
      onSignOut={(everywhere) =>
        void guarded(async () => {
          await api(everywhere ? '/auth/sessions' : '/auth/session', { method: 'DELETE' });
          window.location.hash = '#/signin';
        })
      }
      onEmailChange={(email) => setMe({ ...me, user: { ...me.user, email } })}
      devices={devices}
      newPin={newPin}
      now={now}
      onGeneratePin={() =>
        guarded(async () => {
          // The PIN lives in this response only: shown until "J'ai noté le code", never fetched again.
          setNewPin(await api<NewStaffPin>(`/account/venues/${encodeURIComponent(selected)}/staff-pin`, { method: 'POST' }));
          await reload();
        })
      }
      onDismissPin={() => setNewPin(null)}
      onRevokeDevice={(id) =>
        guarded(async () => {
          await api(`/account/venues/${encodeURIComponent(selected)}/staff-devices/${encodeURIComponent(id)}`, { method: 'DELETE' });
          await loadDevices(selected);
        })
      }
      onDownloadPoster={(ppId, size, fileName) => downloadPoster(selected, ppId, size, fileName)}
    />
  );
}

/**
 * The TV board's optional second voice language for one pickup point (issue #19). The venue default is always spoken
 * first and is not offered as a second language; "Aucune" means the default only. Labels are the language names in
 * their own script, never flags.
 */
export function VoiceLangSetting({
  lang,
  venueDefault,
  pp,
  saved,
  onChange,
}: {
  lang: Lang;
  venueDefault: Lang;
  pp: { id: string; name: string; voice_lang_2: Lang | null };
  saved: boolean;
  onChange: (l: Lang | null) => void;
}) {
  const t = UI[lang];
  const id = `voice-lang-${pp.id}`;
  const value = pp.voice_lang_2 && pp.voice_lang_2 !== venueDefault ? pp.voice_lang_2 : '';
  return (
    <div className="row setting">
      <label htmlFor={id}>{t.voice_lang_2}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value === '' ? null : (e.target.value as Lang))}>
        <option value="">{t.voice_lang_none}</option>
        {SUPPORTED_LANGS.filter((l) => l !== venueDefault).map((l) => (
          <option key={l} value={l} lang={l}>
            {LANG_LABELS[l]}
          </option>
        ))}
      </select>
      <span className="muted">{fill(t.default_lang_is, { lang: LANG_LABELS[venueDefault] })}</span>
      {saved ? (
        <span className="tag" role="status">
          ✓ {t.saved}
        </span>
      ) : null}
    </div>
  );
}
